import React from "react";
import { AlertTriangle, CheckCircle2, Clock, Lock, PencilLine } from "lucide-react";
import { formatWhen, statusMeta } from "./workflowMeta";

/** One card for a plan's state: class, people, status pill and a single message. */
export default function PlanStatusCard({ plan, displayStatus, teacherName, hodName, note }) {
  const meta = statusMeta(plan);
  const cr = plan.last_changes_requested;

  const states = {
    teacher_draft: { Icon: PencilLine, card: "border-gray-200 bg-[#F7FAFF]", icon: "text-[#1565c0]", headline: "Draft", detail: "The teacher has not submitted this plan yet." },
    changes_requested: {
      Icon: AlertTriangle, card: "border-rose-200 bg-rose-50", icon: "text-rose-600",
      headline: `${cr?.by || "A reviewer"} asked for changes${cr?.at ? ` · ${formatWhen(cr.at)}` : ""}`,
      detail: "Waiting for the teacher to update and resubmit.",
    },
    hod_review: {
      Icon: Clock, card: "border-amber-200 bg-amber-50", icon: "text-amber-600",
      headline: "Waiting for the HOD",
      detail: `Submitted${plan.teacherSubmittedAt ? ` ${formatWhen(plan.teacherSubmittedAt)}` : ""}.`,
    },
    admin_review: {
      Icon: Lock, card: "border-blue-200 bg-blue-50", icon: "text-blue-600",
      headline: "Waiting for the admin",
      detail: `The HOD${plan.hodSubmittedAt ? ` approved it ${formatWhen(plan.hodSubmittedAt)}` : " approved it"}.`,
    },
    approved: {
      Icon: CheckCircle2, card: "border-emerald-200 bg-emerald-50", icon: "text-emerald-600",
      headline: `Approved${plan.adminApprovedAt ? ` · ${formatWhen(plan.adminApprovedAt)}` : ""}`,
      detail: "Editing it sends it back to HOD review.",
    },
  };
  const st = states[displayStatus] || states.teacher_draft;

  return (
    <section className={`rounded-2xl border px-6 py-5 ${st.card}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <st.Icon className={`mt-1 h-6 w-6 shrink-0 ${st.icon}`} />
          <div className="min-w-0">
            <h2 className="m-0 text-xl font-semibold text-gray-900">{st.headline}</h2>
            <p className="m-0 mt-1 text-sm text-gray-700">
              {st.detail}
              {note ? ` ${note}` : ""}
            </p>
            <p className="m-0 mt-2 text-xs text-gray-500">
              Teacher: {teacherName || "not assigned"}
              {hodName ? ` · HOD: ${hodName}` : ""}
              {plan.revision > 1 ? ` · revision ${plan.revision}` : ""}
            </p>
            {displayStatus === "changes_requested" && cr?.reason && (
              <p className="mb-0 mt-3 whitespace-pre-wrap rounded-xl bg-white/70 px-4 py-3 text-sm text-rose-900">{cr.reason}</p>
            )}
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-3 py-1 text-sm font-semibold ${meta.pill}`}>{meta.label}</span>
      </div>
    </section>
  );
}
